import { useEffect } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { Layout } from '@components/Layout'
import { DEFAULT_LANG, isLangCode } from '@shared/i18n/languages'
import i18n from '../i18n'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }) }, [pathname])
  return null
}
import { HomePage } from '@pages/HomePage'
import { NotFoundPage } from '@pages/NotFoundPage'
import { DirectionPageLoader } from '@pages/DirectionPage'
import { KontaktyPage } from '@pages/KontaktyPage'
import { ZayavkaPage } from '@pages/ZayavkaPage'
import { RabotyPage } from '@pages/RabotyPage'
import { PolitikaPage } from '@pages/PolitikaPage'
import { ConsentPage } from '@pages/ConsentPage'
import { CasePage } from '@pages/CasePage'
import { CasePageLoader } from '@pages/CasePage/CasePageLoader'
import type { CasePageProps } from '@pages/CasePage'

// ── Демо-кейс: все блоки для визуального тестирования ─────────
const CASE_BLEM1: CasePageProps = {
  category: 'Судоремонт',
  year: '2025',
  title: 'Буксир «Блем-1»',
  subtitle: 'Доковый ремонт корпуса, винто-рулевого комплекса и главного двигателя с освидетельствованием РКО.',
  facts: [
    { k: 'Тип судна',   v: 'Буксир-толкач' },
    { k: 'Заказчик',    v: 'Речная судоходная компания' },
    { k: 'Год',         v: '2025' },
    { k: 'Срок работ',  v: '42 суток' },
    { k: 'Место',       v: 'Dok, собственная база' },
    { k: 'Надзор',      v: 'РКО' },
  ],
  blocks: [
    // Комбинированный блок (файл 1: задача + работы в одном разделе)
    {
      type: 'task-and-works',
      paragraphs: [
        'Судно поступило на плановый доковый ремонт с износом обшивки в районе переменной ватерлинии и повышенной вибрацией валопровода. Требовалось выполнить весь объём работ за одну доковую постановку и предъявить судно Российскому Классификационному Обществу.',
        'Работы выполнены на собственной производственной базе, срок постановки — 42 суток.',
      ],
      items: [
        'Дефектация и замена обшивки корпуса в районе переменной ватерлинии — 18 м²',
        'Демонтаж, ремонт и центровка винто-рулевого комплекса',
        'Переборка главного двигателя с заменой цилиндро-поршневой группы',
        'Балансировка гребного винта и замена дейдвудных втулок',
        'Очистка и окраска подводной части корпуса',
        'Предъявление судна и оформление документов РКО',
      ],
    },
    // 1a — задача (тёмная) + исполнение (белая)
    {
      type: 'task-execution',
      task: 'Судно поступило с износом обшивки в районе переменной ватерлинии и повышенной вибрацией валопровода. Требовалось выполнить весь объём за одну доковую постановку и предъявить судно РКО.',
      execution: 'Заменили 18 м² обшивки, отцентровали винто-рулевой комплекс, перебрали главный двигатель. Судно предъявлено РКО и сдано заказчику за 42 суток — на 6 суток раньше графика.',
    },
    // 1b — этапы-маршрут с пунктирной линией
    {
      type: 'route',
      steps: [
        { title: 'Дефектация',      desc: 'Освидетельствование корпуса и механизмов, составление ремонтной ведомости', duration: '5 суток' },
        { title: 'Корпусные работы', desc: 'Замена обшивки 18 м² в районе переменной ватерлинии, ремонт набора',        duration: '18 суток' },
        { title: 'Механика',        desc: 'Переборка главного двигателя, центровка винто-рулевого комплекса',           duration: '14 суток' },
        { title: 'Сдача',           desc: 'Швартовные испытания, предъявление судна и документов РКО',                  duration: '5 суток' },
      ],
    },
    // 1c — этапы с картинками
    {
      type: 'photo-stages',
      steps: [
        { title: 'Постановка в dok' },
        { title: 'Корпусные работы' },
        { title: 'Ремонт механизмов' },
        { title: 'Спуск и сдача' },
      ],
    },
    // Фото хода работ (3-col сетка)
    {
      type: 'photos',
      items: [{ label: 'Судно в доке' }, { label: 'Замена обшивки' }, { label: 'Переборка двигателя' }],
    },
    // 2a — только задача (отдельно)
    {
      type: 'task',
      paragraphs: [
        'Судно поступило на плановый доковый ремонт с износом обшивки в районе переменной ватерлинии и повышенной вибрацией валопровода. Требовалось выполнить весь объём работ за одну доковую постановку и предъявить судно РКО.',
        'Работы выполнены на собственной производственной базе, срок постановки — 42 суток.',
      ],
    },
    // 2b — только работы (отдельно)
    {
      type: 'works',
      items: [
        'Дефектация и замена обшивки корпуса в районе переменной ватерлинии — 18 м²',
        'Демонтаж, ремонт и центровка винто-рулевого комплекса',
        'Переборка главного двигателя с заменой цилиндро-поршневой группы',
        'Балансировка гребного винта и замена дейдвудных втулок',
        'Очистка и окраска подводной части корпуса',
        'Предъявление судна и оформление документов РКО',
      ],
    },
  ],
  similar: [
    { category: 'Судоремонт',   year: '2024', title: 'Теплоход «Флюц»',            desc: 'Ремонт главного двигателя, судовых систем и электрооборудования' },
    { category: 'Судоремонт',   year: '2024', title: 'Сухогруз РТ-234',             desc: 'Замена обшивки корпуса и набора, освидетельствование РКО' },
    { category: 'Производство', year: '2025', title: 'Гребной вал РТ-234',          desc: 'Изготовление, механическая обработка и балансировка по чертежам' },
    { category: 'Запчасти',     year: '2024', title: 'Комплект дейдвудных втулок',  desc: 'Изготовление и поставка партии втулок для речного флота' },
    { category: 'Производство', year: '2023', title: 'Швартовное устройство',       desc: 'Изготовление и монтаж кнехтов и киповых планок' },
  ],
}

function LocalizedApp() {
  const { lang } = useParams<{ lang: string }>()

  useEffect(() => {
    if (isLangCode(lang)) void i18n.changeLanguage(lang)
  }, [lang])

  if (!isLangCode(lang)) return <Navigate to={`/${DEFAULT_LANG}`} replace />

  return (
    <Layout>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="napravleniya/:slug" element={<DirectionPageLoader />} />
        <Route path="kontakty" element={<KontaktyPage />} />
        <Route path="privacy" element={<PolitikaPage />} />
        <Route path="consent" element={<ConsentPage />} />
        <Route path="zayavka" element={<ZayavkaPage />} />
        <Route path="raboty" element={<RabotyPage />} />
        <Route path="raboty/:slug" element={<CasePageLoader />} />
        <Route path="keis" element={<CasePage {...CASE_BLEM1} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Layout>
  )
}

export function App() {
  return (
    <Routes>
      <Route path="/*" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
      <Route path="/:lang/*" element={<LocalizedApp />} />
    </Routes>
  )
}
